import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
} from "@mui/material";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { getLeagueDaySummaries } from "../../services/aiFeaturesService";
import { AiLeagueDaySummary } from "../../types/Ai";
import "./MatchdaySummary.scss";

interface MatchdaySummaryProps {
  leagueId: number;
  isDesktop?: boolean;
}

// "2026-06-15" → "15 cze 2026" (Polish, matching the rest of the AI content).
function formatDay(matchDay: string): string {
  const date = new Date(`${matchDay}T00:00:00`);
  if (Number.isNaN(date.getTime())) return matchDay;
  return date.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

// Shared "AI Studio" card shell — an accordion (collapsed on mobile, expanded on
// desktop) with the gold kicker and a title in the header. Used for both the real
// recap and the empty-state placeholder so they look and behave identically.
const SummaryCard: React.FC<{
  isDesktop?: boolean;
  title: React.ReactNode;
  children: React.ReactNode;
}> = ({ isDesktop, title, children }) => (
  <Accordion
    className={`matchdaySummary${isDesktop ? " matchdaySummary--desktop" : ""}`}
    defaultExpanded={isDesktop}
    disableGutters
    elevation={0}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreRoundedIcon className="matchdaySummary__expand" />}
      className="matchdaySummary__summary"
    >
      <span className="matchdaySummary__kicker">
        <AutoAwesomeRoundedIcon className="matchdaySummary__sparkle" />
        AI Studio
      </span>
      <span className="matchdaySummary__title">{title}</span>
    </AccordionSummary>
    <AccordionDetails className="matchdaySummary__details">
      {children}
    </AccordionDetails>
  </Accordion>
);

// A humorous AI recap of a league's match day, shown as a collapsible card above
// the ranking table. Defaults to the latest day with prev/next browsing through
// the league's history. Non-critical decoration: shows a "coming soon" placeholder
// when the league has no generated summaries yet.
const MatchdaySummary: React.FC<MatchdaySummaryProps> = ({
  leagueId,
  isDesktop,
}) => {
  const [summaries, setSummaries] = useState<AiLeagueDaySummary[]>([]);
  const [index, setIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (leagueId < 0) return; // league not resolved yet — keep loading
    let cancelled = false;
    setLoading(true);
    getLeagueDaySummaries(leagueId)
      .then((data) => {
        if (cancelled) return;
        // Oldest → newest, so the last entry is the most recent match day.
        const sorted = [...data].sort((a, b) =>
          a.matchDay.localeCompare(b.matchDay)
        );
        setSummaries(sorted);
        setIndex(Math.max(0, sorted.length - 1)); // default to latest
      })
      .catch(() => {
        if (!cancelled) setSummaries([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [leagueId]);

  // Still resolving the league / fetching. On desktop the sidebar reserves its
  // width with a skeleton so the standings table doesn't first paint full width
  // and then reflow once the recap arrives. On mobile (collapsed accordion with
  // no table beside it) render nothing until we know what to show.
  if (loading) {
    if (!isDesktop) return null;
    return (
      <SummaryCard isDesktop title="Podsumowanie dnia">
        <div className="matchdaySummary__loading" aria-hidden>
          <span className="matchdaySummary__loadingLine" />
          <span className="matchdaySummary__loadingLine" />
          <span className="matchdaySummary__loadingLine matchdaySummary__loadingLine--short" />
        </div>
      </SummaryCard>
    );
  }

  const current = summaries[index];

  // No summaries generated yet (e.g. before the tournament has produced any
  // match days) — explain what will appear here.
  if (!current) {
    return (
      <SummaryCard isDesktop={isDesktop} title="Podsumowania dnia">
        <p className="matchdaySummary__text">
          Gdy ruszą Mistrzostwa Świata 2026, codziennie pojawi się tu satyryczne
          podsumowanie dnia w Twojej lidze — kto zgarnął najwięcej punktów, kto
          poległ i kto odrobił straty. Zaglądaj tu po pierwszych meczach!
        </p>
      </SummaryCard>
    );
  }

  const hasMultiple = summaries.length > 1;

  return (
    <SummaryCard
      isDesktop={isDesktop}
      title={`Podsumowanie dnia · ${formatDay(current.matchDay)}`}
    >
      <p className="matchdaySummary__text">{current.content}</p>
      {hasMultiple && (
        <div className="matchdaySummary__nav">
          <IconButton
            aria-label="Poprzedni dzień"
            size="small"
            disabled={index === 0}
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
          >
            <ChevronLeftRoundedIcon />
          </IconButton>
          <span className="matchdaySummary__navLabel">
            Dzień {index + 1} z {summaries.length}
          </span>
          <IconButton
            aria-label="Następny dzień"
            size="small"
            disabled={index === summaries.length - 1}
            onClick={() =>
              setIndex((i) => Math.min(summaries.length - 1, i + 1))
            }
          >
            <ChevronRightRoundedIcon />
          </IconButton>
        </div>
      )}
    </SummaryCard>
  );
};

export default MatchdaySummary;
